import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Wand2, Camera, Scissors, Music, Compass, TrendingUp, BarChart3, Loader2, Crown } from "lucide-react";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { toast } from "sonner";

/**
 * PlyxAILab — `/plyx/ai-lab`
 *
 * Central hub that surfaces every free + Pro creator tool in one place.
 *
 * Sections:
 *   1) Quick Actions Grid (Cam / Studio / AI Studio / Sound Library / Insights)
 *   2) Mood Discover — pick a vibe, browse free curated playlist of clips
 *   3) Trend Forecast — daily-cached AI predictions of upcoming moments
 *   4) Marketplace AI tools — quick links to product/price/service helpers
 */

const MOODS = [
  { slug: "hype",    label: "Hype",    emoji: "🔥" },
  { slug: "chill",   label: "Chill",   emoji: "☁️" },
  { slug: "hustle",  label: "Hustle",  emoji: "💼" },
  { slug: "comedy",  label: "Comedy",  emoji: "🤣" },
  { slug: "news",    label: "News",    emoji: "📰" },
  { slug: "culture", label: "Culture", emoji: "🌍" },
];

const PlyxAILab = () => {
  const navigate = useNavigate();
  const { user } = usePlyxAuth();
  const [mood, setMood] = useState("hype");
  const [moodItems, setMoodItems] = useState([]);
  const [moodLoading, setMoodLoading] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(true);

  // Load mood feed when mood changes.
  useEffect(() => {
    setMoodLoading(true);
    plyxApi.get(`/plyx/ai/mood-discover?mood=${mood}&limit=12`)
      .then((r) => setMoodItems(r.data?.items || []))
      .catch(() => setMoodItems([]))
      .finally(() => setMoodLoading(false));
  }, [mood]);

  // Load trend forecast once on mount (it's already cached server-side 24h).
  useEffect(() => {
    setForecastLoading(true);
    plyxApi.get("/plyx/ai/trend-forecast")
      .then((r) => setForecast(r.data))
      .catch(() => setForecast({ forecast: [], based_on: [] }))
      .finally(() => setForecastLoading(false));
  }, []);

  const TOOLS = [
    { id: "cam",     label: "Plyx Cam",      desc: "AR face filters + record",       to: "/plyx/cam",    icon: Camera,   tint: "from-pink-500 to-rose-500" },
    { id: "studio",  label: "Studio",        desc: "Trim · speed · filter · music",  to: "/plyx/studio", icon: Scissors, tint: "from-cyan-500 to-indigo-500" },
    { id: "upload",  label: "Upload",        desc: "Publish + AI Studio drawer",     to: "/plyx/upload", icon: Wand2,    tint: "from-amber-500 to-pink-500" },
    { id: "insights",label: "Insights",      desc: "30-day stats + 3 AI tips",       to: "/plyx/insights", icon: BarChart3,tint: "from-teal-500 to-emerald-500" },
  ];

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="plyx-ailab-root">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
        {/* Hero */}
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#FFCC2E] to-[#FF3D7F] flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-black"/>
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight">AI Lab</h1>
            <div className="text-xs text-white/60">Tools to make, edit & discover faster</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="plyx-ailab-quick">
          {TOOLS.map((t) => {
            const I = t.icon;
            return (
              <button
                key={t.id} onClick={() => navigate(t.to)}
                className={`text-left bg-gradient-to-br ${t.tint} p-3 rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition`}
                data-testid={`plyx-ailab-quick-${t.id}`}
              >
                <I className="w-5 h-5 mb-1.5"/>
                <div className="font-bold text-sm">{t.label}</div>
                <div className="text-[10px] text-white/80 leading-tight">{t.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Mood Discover */}
        <div className="mt-7" data-testid="plyx-ailab-mood">
          <div className="flex items-center gap-2 mb-2">
            <Compass className="w-4 h-4 text-[#22D3EE]"/>
            <div className="font-bold">Discover by mood</div>
            <span className="text-[10px] text-white/40 ml-auto">free for everyone</span>
          </div>
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2">
            {MOODS.map((m) => (
              <button
                key={m.slug} onClick={() => setMood(m.slug)}
                className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition flex items-center gap-1.5 ${mood === m.slug ? "bg-white text-black" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
                data-testid={`plyx-ailab-mood-${m.slug}`}
              ><span>{m.emoji}</span> {m.label}</button>
            ))}
          </div>
          <div className="mt-3">
            {moodLoading ? (
              <div className="flex items-center justify-center py-10 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2"/> Loading {mood} vibes…</div>
            ) : moodItems.length === 0 ? (
              <div className="text-center text-white/40 text-sm py-8">No clips for this mood yet — try another vibe.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="plyx-ailab-mood-grid">
                {moodItems.map((item) => (
                  <Link
                    key={item.video_id} to={`/plyx/v/${item.video_id}`}
                    className="block rounded-xl overflow-hidden bg-zinc-900 border border-white/10 hover:border-white/30 transition"
                    data-testid={`plyx-ailab-mood-item-${item.video_id}`}
                  >
                    <div className="aspect-[9/16] bg-zinc-800 relative">
                      {item.thumbnail_url ? (
                        <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover"/>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/30"><Sparkles className="w-6 h-6"/></div>
                      )}
                      {item.like_count > 0 && (
                        <div className="absolute bottom-1 left-1 text-[10px] font-bold bg-black/70 px-1.5 py-0.5 rounded-full">❤ {item.like_count}</div>
                      )}
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="text-[10px] text-white/60 font-semibold truncate">@{item.handle || "creator"}</div>
                      <div className="text-[11px] text-white/80 line-clamp-2">{item.caption || ""}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Trend Forecast */}
        <div className="mt-7" data-testid="plyx-ailab-forecast">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-[#FF3D7F]"/>
            <div className="font-bold">7-14 day trend forecast</div>
            <span className="text-[10px] text-white/40 ml-auto">refreshed daily · free</span>
          </div>
          {forecastLoading ? (
            <div className="flex items-center py-8 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2"/> Crystal-balling…</div>
          ) : !forecast?.forecast?.length ? (
            <div className="text-sm text-white/40 py-4">Not enough recent data to forecast — check back tomorrow.</div>
          ) : (
            <div className="space-y-2">
              {forecast.forecast.map((f, i) => (
                <div key={i} className="rounded-2xl bg-gradient-to-br from-white/5 to-white/0 border border-white/10 px-4 py-3" data-testid={`plyx-ailab-forecast-item-${i}`}>
                  <div className="font-bold text-sm">{f.moment}</div>
                  <div className="text-[11px] text-white/60 mt-0.5">{f.why_now}</div>
                  <div className="text-xs text-[#FFCC2E] mt-2"><b>Try:</b> {f.creator_angle}</div>
                  {f.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {f.tags.map((t) => (
                        <Link key={t} to={`/plyx/tag/${encodeURIComponent(t)}`} className="text-[10px] text-white/70 bg-white/10 hover:bg-white/20 px-2 py-0.5 rounded-full">#{t}</Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {forecast.based_on?.length > 0 && (
                <div className="text-[10px] text-white/30 mt-2 px-1">
                  Based on rising: {forecast.based_on.slice(0, 5).map((t) => `#${t.tag}`).join(", ")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Marketplace AI tools */}
        <div className="mt-7" data-testid="plyx-ailab-market">
          <div className="flex items-center gap-2 mb-2">
            <Crown className="w-4 h-4 text-[#FFCC2E]"/>
            <div className="font-bold">Marketplace AI</div>
            <span className="text-[10px] text-white/40 ml-auto">free for sellers</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            <MarketTile
              title="Product description"
              desc="Turn bullet points into a sales-ready blurb."
              endpoint="/plyx/ai/product-description"
              kind="product"
              user={user}
              navigate={navigate}
            />
            <MarketTile
              title="Smart price suggest"
              desc="Fair price band for your item."
              endpoint="/plyx/ai/price-suggest"
              kind="price"
              user={user}
              navigate={navigate}
            />
            <MarketTile
              title="Service listing writer"
              desc="Polished title + bullets + CTA."
              endpoint="/plyx/ai/service-listing"
              kind="service"
              user={user}
              navigate={navigate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Inline lightweight marketplace AI test tiles — open a small in-page
// composer modal so sellers can preview the output before going to /create.
const MarketTile = ({ title, desc, endpoint, kind, user, navigate }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => { if (!user) { toast.error("Sign in first"); navigate("/plyx/login?next=/plyx/ai-lab"); return; } setOpen(true); }}
        className="text-left rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition p-3"
        data-testid={`plyx-ailab-market-${kind}`}
      >
        <div className="font-bold text-sm">{title}</div>
        <div className="text-[11px] text-white/60 mt-0.5">{desc}</div>
      </button>
      {open && <MarketComposer kind={kind} endpoint={endpoint} title={title} onClose={() => setOpen(false)}/>}
    </>
  );
};

const MarketComposer = ({ kind, endpoint, title, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Per-kind state
  const [name, setName] = useState("");
  const [bullets, setBullets] = useState("");
  const [category, setCategory] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [notes, setNotes] = useState("");
  const [deliverables, setDeliverables] = useState("");
  const [audience, setAudience] = useState("");

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      let payload = {};
      if (kind === "product") {
        if (name.length < 2 || !bullets.trim()) { toast.error("Name + at least one bullet"); setBusy(false); return; }
        payload = { name, bullet_points: bullets.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 8), category: category || undefined, audience: audience || undefined };
      } else if (kind === "price") {
        if (name.length < 2 || !category) { toast.error("Name + category"); setBusy(false); return; }
        payload = { name, category, currency, notes: notes || undefined };
      } else {
        if (name.length < 3 || !deliverables.trim()) { toast.error("Name + at least one deliverable"); setBusy(false); return; }
        payload = { service_name: name, deliverables: deliverables.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 8), audience: audience || undefined };
      }
      const r = await plyxApi.post(endpoint, payload);
      setResult(r.data);
    } catch {
      toast.error("AI tool failed — try again");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center sm:justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-zinc-900 border-t sm:border border-white/15 sm:rounded-2xl rounded-t-3xl text-white p-5 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`plyx-market-composer-${kind}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold">{title}</div>
          <button onClick={onClose} className="text-white/40 hover:text-white text-sm">✕</button>
        </div>
        <div className="space-y-2 text-sm">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Item / service name"
            className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 placeholder-white/30 focus:outline-none focus:border-[#FFCC2E]" data-testid="plyx-market-name"/>
          {kind === "product" && (
            <>
              <textarea value={bullets} onChange={(e) => setBullets(e.target.value)} placeholder={"Key bullet points (one per line)\nHandmade in Lagos\nPure cotton\nMachine washable"} rows={4}
                className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 placeholder-white/30 focus:outline-none focus:border-[#FFCC2E]" data-testid="plyx-market-bullets"/>
              <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category (e.g. fashion)" className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 placeholder-white/30" data-testid="plyx-market-category"/>
              <input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Audience (optional)" className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 placeholder-white/30" data-testid="plyx-market-audience"/>
            </>
          )}
          {kind === "price" && (
            <>
              <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category (e.g. handmade jewellery)" className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 placeholder-white/30" data-testid="plyx-market-category"/>
              <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))} placeholder="GBP / USD / NGN" className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 placeholder-white/30" data-testid="plyx-market-currency"/>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (size, condition, materials…)" rows={3} className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 placeholder-white/30" data-testid="plyx-market-notes"/>
            </>
          )}
          {kind === "service" && (
            <>
              <textarea value={deliverables} onChange={(e) => setDeliverables(e.target.value)} placeholder={"Deliverables (one per line)\n3 logo concepts\nVector files\n2 rounds of revisions"} rows={4}
                className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 placeholder-white/30" data-testid="plyx-market-deliverables"/>
              <input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Audience (optional)" className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 placeholder-white/30" data-testid="plyx-market-audience"/>
            </>
          )}
        </div>
        <button onClick={run} disabled={busy} className="w-full mt-4 bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold rounded-xl py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="plyx-market-run">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>} Run
        </button>
        {result && (
          <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3 text-sm whitespace-pre-line" data-testid="plyx-market-result">
            {kind === "product"  && result.description}
            {kind === "price"    && (
              <div>
                <div className="font-bold text-base mb-1">{result.currency} {result.low?.toFixed(0)} – {result.high?.toFixed(0)}</div>
                <div className="text-xs text-white/60">Suggested: {result.currency} {result.mid?.toFixed(0)}</div>
                <div className="text-xs text-white/50 mt-2">{result.rationale}</div>
              </div>
            )}
            {kind === "service"  && (
              <div>
                <div className="font-bold text-base mb-1">{result.title}</div>
                <div className="text-xs text-white/70 mb-2">{result.intro}</div>
                {result.bullets?.length > 0 && (
                  <ul className="text-xs text-white/80 list-disc pl-4 space-y-0.5 mb-2">
                    {result.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
                <div className="text-xs font-bold text-[#FFCC2E]">{result.cta}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlyxAILab;
